import { HOT_CAPACITY_LABEL } from '~/constants'
import { filterEmptyLegsByOrigin, MAX_EMPTY_LEG_DISTANCE_FILTER } from '~/helpers/empty-legs/filter-by-origin'

import {
    PublicAirportLocation, PublicEmptyLeg, PublicEmptyLegGroup, PublicEmptyLegResults, PublicEmptyLegSearch,
} from './types'

export const AVAILABLE_GROUP_TITLE = `Available ${HOT_CAPACITY_LABEL}`

const getAirportCode = (airport: PublicAirportLocation | null) => airport?.iata_code || airport?.icao_code || ''
const normalizeAirportCode = (code?: string) => code?.trim().toUpperCase() || ''

const hasMatchingAirport = (
    emptyLegAirportId: string | undefined,
    emptyLegCodes: Array<string | undefined>,
    selectedAirportId: string,
    selectedAirport: PublicAirportLocation,
) => {
    if (emptyLegAirportId && selectedAirportId) {
        return String(emptyLegAirportId) === String(selectedAirportId)
    }

    const selectedCodes = new Set([
        normalizeAirportCode(selectedAirport.iata_code),
        normalizeAirportCode(selectedAirport.icao_code),
    ].filter(Boolean))

    return emptyLegCodes.some((code) => selectedCodes.has(normalizeAirportCode(code)))
}

const hasMatchingOrigin = (emptyLeg: PublicEmptyLeg, search: PublicEmptyLegSearch) => (
    search.from_location !== null
    && hasMatchingAirport(
        emptyLeg.fromId,
        [emptyLeg.fromIata_code, emptyLeg.fromIcao_code],
        search.from,
        search.from_location,
    )
)

export const isExactPublicEmptyLegMatch = (
    emptyLeg: PublicEmptyLeg,
    search: PublicEmptyLegSearch,
) => (
    hasMatchingOrigin(emptyLeg, search)
    && search.destination_location !== null
    && hasMatchingAirport(
        emptyLeg.destinationId,
        [emptyLeg.destinationIata_code, emptyLeg.destinationIcao_code],
        search.destination,
        search.destination_location,
    )
)

const createGroup = (
    title: string,
    matchType: PublicEmptyLegGroup['matchType'],
    emptyLegs: PublicEmptyLeg[],
): PublicEmptyLegGroup | null => (
    emptyLegs.length > 0 ? { title, matchType, emptyLegs } : null
)

export const getPublicEmptyLegType = (requestType: string) => (
    requestType.toLowerCase().includes('ad hoc') ? 'Ad hoc' : 'Empty leg'
)

export const getPublicEmptyLegCapacity = ({ volume, weight }: PublicEmptyLeg) => `${Math.round(weight / 1000)} t / ${volume} m3`

export const buildPublicEmptyLegResults = (emptyLegs: PublicEmptyLeg[], search: PublicEmptyLegSearch): PublicEmptyLegResults => {
    const fromLocation = search.from_location
    const destinationLocation = search.destination_location
    const fromCode = getAirportCode(fromLocation)
    const destinationCode = getAirportCode(destinationLocation)

    if (!fromLocation || !destinationLocation || !fromCode || !destinationCode || !search.from || !search.destination) {
        if (emptyLegs.length === 0) {
            return {
                kind: 'marketEmpty',
                message: 'Here’s what’s available on the market:',
                description: 'There are no empty legs available yet.',
            }
        }

        return {
            kind: 'results',
            message: 'Here’s what’s available on the market:',
            groups: [{ title: AVAILABLE_GROUP_TITLE, matchType: 'market', emptyLegs }],
        }
    }

    const exactMatches = emptyLegs.filter((emptyLeg) => isExactPublicEmptyLegMatch(emptyLeg, search))
    const fromMatches = emptyLegs.filter((emptyLeg) => (
        hasMatchingOrigin(emptyLeg, search) && !isExactPublicEmptyLegMatch(emptyLeg, search)
    ))
    const excludedIds = new Set([...exactMatches, ...fromMatches].map(({ id }) => id))
    const nearbyCandidates = emptyLegs.filter(({ id }) => !excludedIds.has(id))
    const nearbyMatches = filterEmptyLegsByOrigin(nearbyCandidates, [fromLocation])
    const groups = [
        createGroup('Exact matches', 'exact', exactMatches),
        createGroup(`From ${fromCode}`, 'origin', fromMatches),
        createGroup(`Near ${fromCode} • within ${MAX_EMPTY_LEG_DISTANCE_FILTER} km`, 'nearby', nearbyMatches),
    ].filter((group): group is PublicEmptyLegGroup => group !== null)

    if (groups.length === 0) {
        return {
            kind: 'noMatches',
            title: `No matches for ${fromCode} → ${destinationCode}`,
            description: 'No matches on this date. Please try different dates or parameters.',
        }
    }

    return {
        kind: 'results',
        message: exactMatches.length > 0
            ? `${exactMatches.length} exact ${exactMatches.length === 1 ? 'match' : 'matches'} for ${fromCode} → ${destinationCode}`
            : `No exact matches for ${fromCode} → ${destinationCode} right now. Here’s what’s available on the market:`,
        groups,
    }
}
